import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../store';

interface SubscriptionState {
  loading: boolean;
  error: string | null;
  subscription: {
    tier: string;
    status: string;
    periodEnd: string | null;
  } | null;
}

const initialState: SubscriptionState = {
  loading: false,
  error: null,
  subscription: null,
};

// Async thunks
export const createCheckoutSession = createAsyncThunk(
  'subscription/createCheckoutSession',
  async (tier: string, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/subscription/create-checkout-session', { tier });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create checkout session');
    }
  }
);

export const createPortalSession = createAsyncThunk(
  'subscription/createPortalSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/subscription/create-portal-session');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create portal session');
    }
  }
);

export const getSubscription = createAsyncThunk(
  'subscription/getSubscription',
  async (subscriptionId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/subscription/subscription/${subscriptionId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to get subscription');
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create checkout session
      .addCase(createCheckoutSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, action) => {
        state.loading = false;
        // Redirect to Stripe checkout
        window.location.href = action.payload.url;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create portal session
      .addCase(createPortalSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPortalSession.fulfilled, (state, action) => {
        state.loading = false;
        // Redirect to Stripe portal
        window.location.href = action.payload.url;
      })
      .addCase(createPortalSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get subscription
      .addCase(getSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = {
          tier: action.payload.tier,
          status: action.payload.status,
          periodEnd: action.payload.current_period_end,
        };
      })
      .addCase(getSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = subscriptionSlice.actions;

// Selectors
export const selectSubscription = (state: RootState) => state?.subscription?.subscription || null;
export const selectSubscriptionLoading = (state: RootState) => state?.subscription?.loading || false;
export const selectSubscriptionError = (state: RootState) => state?.subscription?.error || null;

export default subscriptionSlice.reducer; 